// RFC-4180 CSV serializer for the M10 admin "download errors only" flow and
// for any future export that needs to produce a CSV file the user can re-open
// in Excel or Google Sheets. Mirrors the tolerance of `parseCsv` in
// `csv-parse.ts` so a round-trip serialize → parse yields the original rows.
//
// Quoting rules (RFC-4180):
//   - A field is quoted if it contains a comma, a double-quote, a CR or a LF.
//   - Double-quotes inside a quoted field are escaped by doubling: " → "".
//   - Empty/undefined cells become an empty field (two consecutive commas).
//
// Line endings: LF only. Excel on Windows is tolerant of LF-only CSVs and
// Google Sheets normalises to LF on import, so emitting CRLF would be needless
// noise that breaks string-equality assertions in tests.

const NEEDS_QUOTE = /[",\r\n]/;

function escapeField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (s === '') return '';
  if (NEEDS_QUOTE.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Serialize a list of objects to a CSV string. Column order is dictated by
 * `headers` — values are looked up by key, missing keys produce empty cells.
 *
 * The output always ends with a trailing newline so that piping into another
 * tool (e.g. cat) or appending lines later behaves as expected.
 */
export function serializeRowsToCsv(
  headers: string[],
  rows: Record<string, string | number | null | undefined>[],
): string {
  const lines: string[] = [];
  lines.push(headers.map(escapeField).join(','));
  for (const row of rows) {
    lines.push(headers.map((h) => escapeField(row[h])).join(','));
  }
  return lines.join('\n') + '\n';
}
